import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import {
  FaArrowLeft,
  FaBookOpen,
  FaCalendarAlt,
  FaCheckCircle,
  FaEdit,
  FaFeatherAlt,
  FaSearch,
  FaStar,
  FaThumbsUp,
  FaTrash,
  FaUser,
} from "react-icons/fa";
import { supabase } from "../../../supabaseClient";
import { deleteArticleBodyFromR2 } from "../../../services/r2ArticleService";
import { useUser } from "../../../UserContext";
import NewArticleModal from "../../articles/NewArticleModal";
import InsightsBackButton from "../shared/InsightsBackButton";
import "./ArticlesPage.css";

const GUIDE_AUTHOR_WORKSPACE_STYLES = `
  .hobbli-guide-author-page {
    width: min(1180px, calc(100% - 28px));
    margin: 22px auto 52px;
    color: var(--text, #111827);
  }

  .hobbli-guide-author-page,
  .hobbli-guide-author-page * {
    box-sizing: border-box;
  }

  .hobbli-guide-author-shell {
    overflow: visible;
    border: 1px solid var(--border-color, #dfe4ea);
    border-radius: 24px;
    background: var(--card-bg, #ffffff);
  }

  .hobbli-guide-author-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    padding: 18px 22px;
    border-bottom: 1px solid var(--border-color, #dfe4ea);
    border-radius: 24px 24px 0 0;
    background:
      radial-gradient(circle at 88% 10%, rgba(38, 132, 255, 0.11), transparent 30%),
      var(--card-bg, #ffffff);
  }

  .hobbli-guide-author-header__copy {
    min-width: 0;
  }

  .hobbli-guide-author-header__eyebrow {
    display: block;
    margin-bottom: 4px;
    color: #2684ff;
    font-size: .68rem;
    font-weight: 850;
    letter-spacing: .1em;
    text-transform: uppercase;
  }

  .hobbli-guide-author-header h1 {
    margin: 0;
    color: var(--text, #111827);
    font-size: clamp(1.55rem, 4vw, 2.35rem);
    line-height: 1.08;
    letter-spacing: -.045em;
  }

  .hobbli-guide-author-header p {
    max-width: 720px;
    margin: 7px 0 0;
    color: color-mix(in srgb, var(--text, #111827) 62%, transparent);
    font-size: .82rem;
    line-height: 1.55;
  }

  .hobbli-guide-author-back {
    flex: 0 0 auto;
    min-height: 42px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 9px 13px;
    border: 1px solid var(--border-color, #dfe4ea);
    border-radius: 11px;
    background: color-mix(in srgb, var(--card-bg, #ffffff) 94%, var(--text, #111827));
    color: var(--text, #111827);
    font: inherit;
    font-size: .76rem;
    font-weight: 800;
    cursor: pointer;
  }

  .hobbli-guide-author-workspace {
    min-height: 680px;
    padding: clamp(18px, 4vw, 38px);
    background:
      linear-gradient(
        180deg,
        color-mix(in srgb, var(--card-bg, #ffffff) 98%, var(--text, #111827)) 0%,
        var(--card-bg, #ffffff) 100%
      );
    border-radius: 0 0 24px 24px;
  }

  .hobbli-guide-author-workspace::before {
    content: "AUTHOR WORKSPACE";
    display: block;
    margin-bottom: 12px;
    color: color-mix(in srgb, var(--text, #111827) 45%, transparent);
    font-size: .62rem;
    font-weight: 850;
    letter-spacing: .13em;
  }

  body.dark .hobbli-guide-author-shell,
  .dark .hobbli-guide-author-shell {
    background: #111827;
    border-color: #374151;
  }

  body.dark .hobbli-guide-author-header,
  .dark .hobbli-guide-author-header {
    background:
      radial-gradient(circle at 88% 10%, rgba(38, 132, 255, .13), transparent 30%),
      #111827;
    border-color: #374151;
  }

  body.dark .hobbli-guide-author-header h1,
  .dark .hobbli-guide-author-header h1 {
    color: #f3f4f6;
  }

  body.dark .hobbli-guide-author-header p,
  .dark .hobbli-guide-author-header p {
    color: #aeb8c5;
  }

  body.dark .hobbli-guide-author-back,
  .dark .hobbli-guide-author-back {
    background: #182231;
    color: #f3f4f6;
    border-color: #3d4959;
  }

  body.dark .hobbli-guide-author-workspace,
  .dark .hobbli-guide-author-workspace {
    background: #0f1722;
  }

  @media (max-width: 700px) {
    .hobbli-guide-author-page {
      width: calc(100% - 18px);
      margin-top: 10px;
    }

    .hobbli-guide-author-header {
      align-items: stretch;
      flex-direction: column-reverse;
      padding: 18px;
    }

    .hobbli-guide-author-back {
      width: 100%;
    }

    .hobbli-guide-author-workspace {
      min-height: 0;
      padding: 12px;
    }
  }
`;

// Article permissions are intentionally centralized here so the UI and
// action handlers use the same rules.
//
// Existing Hobbli role IDs preserved from the prior ArticlesPage:
// 1 = Superuser
// 2 = Admin
// 4 = Contributor
// 5 = Moderator
const ARTICLE_CREATE_LEVELS = new Set([1, 4, 5]);

const canCreateArticle = (user) => {
  const levelId = Number(user?.user_level_id || 0);
  return ARTICLE_CREATE_LEVELS.has(levelId);
};

const canDeleteArticle = (user, article) => {
  const levelId = Number(user?.user_level_id || 0);
  if (!article) return false;

  if ([1, 2].includes(levelId)) return true;
  if ([4, 5].includes(levelId) && article.source === "user") return true;
  if (levelId === 4 && article.user_id === user?.id) return true;

  return false;
};

const canEditArticle = (user, article) => {
  const levelId = Number(user?.user_level_id || 0);
  if (!article) return false;

  if ([1, 2].includes(levelId)) return true;
  if (levelId === 5 && article.source === "user") return true;
  if (levelId === 4 && article.author_id === user?.id) return true;

  return false;
};

const formatDate = (value) => {
  if (!value) return "Date unavailable";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date unavailable";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export default function ArticlesPage() {
  const { user } = useUser();

  const [articles, setArticles] = useState([]);
  const [uiNotice, setUiNotice] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("hobbli");
  const [authorFilter, setAuthorFilter] = useState("all");
  const [sortBy, setSortBy] = useState("published_at");
  const [searchTerm, setSearchTerm] = useState("");

  const [selectedArticle, setSelectedArticle] = useState(null);
  const [articleBody, setArticleBody] = useState("");
  const [loadingBody, setLoadingBody] = useState(false);

  const [userUpvotes, setUserUpvotes] = useState([]);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (showArticleModal && !canCreateArticle(user)) {
      setShowArticleModal(false);
    }
  }, [showArticleModal, user]);

  useEffect(() => {
    if (showArticleModal) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
  }, [showArticleModal]);

  useEffect(() => {
    const fetchCategories = async () => {
      const { data, error } = await supabase
        .from("article_categories")
        .select("*");

      if (error) console.error("Error fetching categories:", error);
      else setCategories(data || []);
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchArticles = async () => {
      const { data, error } = await supabase
        .from("articles_with_authors")
        .select("*")
        .order(sortBy, { ascending: false });

      if (error) {
        console.error("Error fetching articles:", error);
        return;
      }

      if (!data) {
        setArticles([]);
        return;
      }

      const pinnedSlug = "welcome_to_guides";
      const pinned = data.find((a) => a.slug === pinnedSlug);
      const others = data.filter((a) => a.slug !== pinnedSlug);
      setArticles(pinned ? [pinned, ...others] : data);
    };

    fetchArticles();
  }, [sortBy]);

  useEffect(() => {
    if (!user) {
      setUserUpvotes([]);
      return;
    }

    const fetchUpvotes = async () => {
      const { data, error } = await supabase
        .from("social_interactions")
        .select("target_id")
        .eq("user_id", user.id)
        .eq("target_type", "article")
        .eq("interaction_type", "helpful");

      if (error) {
        console.error("Error fetching user upvotes:", error);
        return;
      }

      setUserUpvotes((data || []).map((d) => String(d.target_id)).filter(Boolean));
    };

    fetchUpvotes();
  }, [user]);

  useEffect(() => {
    const hasR2Body =
      selectedArticle?.storage_provider === "r2" &&
      Boolean(selectedArticle?.object_key);

    if (!hasR2Body) {
      setArticleBody("");
      return;
    }

    const fetchBody = async () => {
      setLoadingBody(true);

      try {
        const encodedObjectKey = String(selectedArticle.object_key)
          .split("/")
          .map((segment) => encodeURIComponent(segment))
          .join("/");

        const url = `https://media.hobbli.com/${encodedObjectKey}`;

        const res = await fetch(url);
        if (!res.ok) throw new Error("Failed to fetch article content.");

        const contentType = res.headers.get("content-type") || "";
        const text = contentType.includes("application/json")
          ? JSON.stringify(await res.json(), null, 2)
          : await res.text();

        setArticleBody(String(text));
      } catch (err) {
        console.error("Failed to fetch article body:", err);
        setArticleBody("Failed to load article content.");
      } finally {
        setLoadingBody(false);
      }
    };

    fetchBody();
  }, [selectedArticle]);

  const sortedAuthors = useMemo(() => {
    return Object.entries(
      articles.reduce((acc, article) => {
        const name = article.author_display_name || "Unknown";
        acc[name] = (acc[name] || 0) + (article.upvotes_count || 0);
        return acc;
      }, {})
    )
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name);
  }, [articles]);

  const sortedArticles = useMemo(() => {
    const filtered = articles.filter((a) => {
      const matchesCategory =
        selectedCategory === "all" ||
        String(a.category_id) === String(selectedCategory);

      const matchesSource =
        sourceFilter === "all" || a.source === sourceFilter;

      const matchesAuthor =
        authorFilter === "all" ||
        (authorFilter === "Unknown" && !a.author_display_name) ||
        a.author_display_name === authorFilter;

      const q = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (a.title || "").toLowerCase().includes(q) ||
        (a.summary || "").toLowerCase().includes(q) ||
        (a.author_display_name || "").toLowerCase().includes(q);

      return (
        matchesCategory && matchesSource && matchesAuthor && matchesSearch
      );
    });

    return [...filtered].sort((a, b) => {
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;

      if (sortBy === "published_at") {
        return new Date(b.published_at || 0) - new Date(a.published_at || 0);
      }

      if (sortBy === "upvotes_count") {
        return (b.upvotes_count || 0) - (a.upvotes_count || 0);
      }

      return 0;
    });
  }, [
    articles,
    selectedCategory,
    sourceFilter,
    authorFilter,
    searchTerm,
    sortBy,
  ]);

  const handleArticleUpdated = (updatedArticle, updatedMarkdown) => {
    if (!updatedArticle?.id) return;

    setArticles((prev) =>
      prev.map((article) =>
        article.id === updatedArticle.id
          ? { ...article, ...updatedArticle }
          : article
      )
    );

    setSelectedArticle((prev) =>
      prev?.id === updatedArticle.id
        ? { ...prev, ...updatedArticle }
        : prev
    );

    if (typeof updatedMarkdown === "string") {
      setArticleBody(updatedMarkdown);
    }

    setIsEditing(false);
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  };

  const handleDeleteArticle = async (article) => {
    if (!article?.id) return;

    const confirmed = window.confirm(
      `Delete "${article.title}"? This cannot be undone.`
    );

    if (!confirmed) return;

    try {
      // R2-backed articles: remove the current R2 body first through the
      // authenticated server-side deletion endpoint.
      //
      // Legacy Supabase-backed article bodies are intentionally NOT removed
      // during the storage migration.
      if (
        article.storage_provider === "r2" &&
        article.object_key
      ) {
        await deleteArticleBodyFromR2(article.id);
      }

      const { error: deleteError } = await supabase
        .from("articles")
        .delete()
        .eq("id", article.id);

      if (deleteError) {
        throw deleteError;
      }

      setArticles((prev) =>
        prev.filter((item) => item.id !== article.id)
      );

      if (selectedArticle?.id === article.id) {
        setSelectedArticle(null);
        setArticleBody("");
      }
    } catch (error) {
      console.error("Failed to delete guide:", error);

      setUiNotice({ type: "error", message: `Failed to delete guide: ${error?.message || "Unknown error"}` });
    }
  };


  const handleHelpful = async (articleId) => {
    if (!user) {
      setUiNotice({ type: "error", message: "You must be logged in to mark an article Helpful." });
      return;
    }

    const { data: updatedArticle, error } = await supabase
      .rpc("handle_article_upvote_toggle", {
        p_user_id: user.id,
        p_article_id: articleId,
      })
      .single();

    if (error) {
      console.error("Article Helpful toggle failed:", error.message);
      return;
    }

    setArticles((prev) =>
      prev.map((a) =>
        a.id === articleId
          ? { ...a, upvotes_count: updatedArticle.upvotes_count }
          : a
      )
    );

    if (selectedArticle?.id === articleId) {
      setSelectedArticle((prev) => ({
        ...prev,
        upvotes_count: updatedArticle.upvotes_count,
      }));
    }

    const articleKey = String(articleId);
    setUserUpvotes((prev) =>
      prev.includes(articleKey)
        ? prev.filter((id) => id !== articleKey)
        : [...prev, articleKey]
    );
  };

  const openArticle = (article) => {
    setSelectedArticle(article);
    setIsEditing(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeArticle = () => {
    setSelectedArticle(null);
    setArticleBody("");
    setIsEditing(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (showArticleModal && canCreateArticle(user)) {
    return (
      <main className="hobbli-guide-author-page">
        <style>{GUIDE_AUTHOR_WORKSPACE_STYLES}</style>

        <section className="hobbli-guide-author-shell">
          <header className="hobbli-guide-author-header">
            <div className="hobbli-guide-author-header__copy">
              <span className="hobbli-guide-author-header__eyebrow">
                Hobbli Contributor Studio
              </span>
              <h1>Write a Guide</h1>
              <p>
                Give yourself room to build something useful. Draft the title,
                summary, category, and full guide below, then publish when it is ready.
              </p>
            </div>

            <button
              className="hobbli-guide-author-back"
              onClick={() => setShowArticleModal(false)}
            >
              <FaArrowLeft /> Back to Guides
            </button>
          </header>

          <div className="hobbli-guide-author-workspace">
            <NewArticleModal
              user={user}
              onClose={() => setShowArticleModal(false)}
              onArticleCreated={(newArticle) => {
                setArticles((prev) => [newArticle, ...prev]);
                setShowArticleModal(false);
                if (newArticle) {
                  setSelectedArticle(newArticle);
                }
              }}
            />
          </div>
        </section>
      </main>
    );
  }

  if (
    selectedArticle &&
    isEditing &&
    canEditArticle(user, selectedArticle)
  ) {
    return (
      <main className="hobbli-guide-author-page">
        <style>{GUIDE_AUTHOR_WORKSPACE_STYLES}</style>

        <section className="hobbli-guide-author-shell">
          <header className="hobbli-guide-author-header">
            <div className="hobbli-guide-author-header__copy">
              <span className="hobbli-guide-author-header__eyebrow">
                Hobbli Contributor Studio
              </span>
              <h1>Edit Guide</h1>
              <p>
                Update the title, summary, category, or article itself. The
                published guide remains unchanged until you save.
              </p>
            </div>

            <button
              className="hobbli-guide-author-back"
              onClick={() => {
                setIsEditing(false);
                window.scrollTo({ top: 0, left: 0, behavior: "auto" });
              }}
            >
              <FaArrowLeft /> Cancel Editing
            </button>
          </header>

          <div className="hobbli-guide-author-workspace">
            <NewArticleModal
              user={user}
              mode="edit"
              initialArticle={selectedArticle}
              initialBodyMarkdown={articleBody}
              loadingInitialBody={loadingBody}
              canEditExisting={canEditArticle(user, selectedArticle)}
              onClose={() => {
                setIsEditing(false);
                window.scrollTo({ top: 0, left: 0, behavior: "auto" });
              }}
              onArticleUpdated={handleArticleUpdated}
            />
          </div>
        </section>
      </main>
    );
  }

  if (selectedArticle) {
    const markedHelpful = userUpvotes.includes(String(selectedArticle.id));

    return (
      <main className="hobbli-articles">
      {uiNotice ? (
        <div role="status" style={{ margin: "0 0 14px", padding: "10px 12px", borderRadius: "8px", background: uiNotice.type === "error" ? "rgba(220, 53, 69, 0.10)" : "rgba(47, 128, 237, 0.10)", color: uiNotice.type === "error" ? "#b42318" : "inherit", fontSize: ".88rem" }}>
          {uiNotice.message}
        </div>
      ) : null}
        <article className="hobbli-article-reader">
          <div className="hobbli-article-reader__topbar">
            <button onClick={closeArticle}>
              <FaArrowLeft /> All Guides
            </button>

            <div className="hobbli-article-reader__admin-actions">
              {canEditArticle(user, selectedArticle) && !isEditing && (
                <button
                  onClick={() => {
                    setIsEditing(true);
                    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
                  }}
                >
                  <FaEdit /> Edit
                </button>
              )}

              {canDeleteArticle(user, selectedArticle) && (
                <button
                  className="hobbli-article-reader__delete"
                  onClick={() => handleDeleteArticle(selectedArticle)}
                >
                  <FaTrash /> Delete
                </button>
              )}
            </div>
          </div>

          <header className="hobbli-article-reader__header">
            <div className="hobbli-article-reader__badges">
              <span>
                {selectedArticle.source === "hobbli" ? (
                  <>
                    <FaCheckCircle /> Hobbli Official
                  </>
                ) : (
                  <>
                    <FaFeatherAlt /> Community Guide
                  </>
                )}
              </span>

              {selectedArticle.featured && (
                <span className="hobbli-article-reader__featured">
                  <FaStar /> Featured
                </span>
              )}
            </div>

            <>
              <h1>{selectedArticle.title || "Untitled Article"}</h1>
              <p className="hobbli-article-reader__summary">
                {selectedArticle.summary || "No summary available."}
              </p>
            </>

            <div className="hobbli-article-reader__meta">
              <span>
                <FaUser />
                {selectedArticle.author_display_name || "Unknown"}
              </span>
              <span>
                <FaCalendarAlt />
                {formatDate(selectedArticle.published_at)}
              </span>
            </div>
          </header>

          <div className="hobbli-article-reader__layout">
            <div className="hobbli-article-reader__content">
              {loadingBody ? (
                <div className="hobbli-article-reader__loading">
                  Loading article...
                </div>
              ) : (
                <ReactMarkdown>{articleBody || ""}</ReactMarkdown>
              )}
            </div>

            <aside className="hobbli-article-reader__sidebar">
              <div className="hobbli-article-appreciation">
                <span className="hobbli-article-appreciation__eyebrow">
                  Was this useful?
                </span>
                <h3>Help the best guides rise.</h3>
                <p>
                  Mark useful articles Helpful so other collectors can find the
                  strongest resources faster.
                </p>

                <button
                  className={markedHelpful ? "is-active" : ""}
                  onClick={() => handleHelpful(selectedArticle.id)}
                >
                  <FaThumbsUp />
                  {markedHelpful ? "Helpful" : "Mark Helpful"}
                  <strong>{selectedArticle.upvotes_count || 0}</strong>
                </button>
              </div>

              <div className="hobbli-article-community-note">
                <FaFeatherAlt />
                <div>
                  <strong>Community knowledge is coming.</strong>
                  <p>
                    Reactions, comments, contributor recognition, and richer
                    author profiles can build on this reading experience next.
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </article>
      </main>
    );
  }

  return (
    <main className="hobbli-articles">
      {uiNotice ? (
        <div role="status" style={{ margin: "0 0 14px", padding: "10px 12px", borderRadius: "8px", background: uiNotice.type === "error" ? "rgba(220, 53, 69, 0.10)" : "rgba(47, 128, 237, 0.10)", color: uiNotice.type === "error" ? "#b42318" : "inherit", fontSize: ".88rem" }}>
          {uiNotice.message}
        </div>
      ) : null}
      <section className="hobbli-articles__hero">
        <div>
          <span className="hobbli-articles__eyebrow">Hobbli Guides</span>
          <h1>Knowledge worth collecting.</h1>
          <p>
            Practical collecting guides from Hobbli and the community—built to
            help collectors learn, protect their collections, and share what
            they know.
          </p>
        </div>

        <div className="hobbli-articles__hero-icon" aria-hidden="true">
          <FaBookOpen />
        </div>
      </section>

      <div className="hobbli-articles__toolbar-row">
        <InsightsBackButton />

        {canCreateArticle(user) && (
          <button
            className="hobbli-articles__create"
            onClick={() => {
              if (canCreateArticle(user)) {
                window.scrollTo({ top: 0, left: 0, behavior: "auto" });
                setShowArticleModal(true);
              }
            }}
          >
            <FaFeatherAlt /> Write a Guide
          </button>
        )}
      </div>

      <section className="hobbli-articles__filters">
        <div className="hobbli-articles__search">
          <FaSearch />
          <input
            type="search"
            placeholder="Search guides or authors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          aria-label="Filter by category"
        >
          <option value="all">All Categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>

        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          aria-label="Filter by source"
        >
          <option value="all">All Articles</option>
          <option value="hobbli">Hobbli Official</option>
          <option value="user">Community</option>
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          aria-label="Sort articles"
        >
          <option value="published_at">Newest</option>
          <option value="upvotes_count">Most Helpful</option>
        </select>

        <select
          value={authorFilter}
          onChange={(e) => setAuthorFilter(e.target.value)}
          aria-label="Filter by author"
        >
          <option value="all">All Authors</option>
          {sortedAuthors.map((author) => (
            <option key={author} value={author}>
              {author}
            </option>
          ))}
        </select>
      </section>

      <div className="hobbli-articles__results-heading">
        <div>
          <span className="hobbli-articles__eyebrow">Library</span>
          <h2>Collector guides</h2>
        </div>
        <span>
          {sortedArticles.length} {sortedArticles.length === 1 ? "guide" : "guides"}
        </span>
      </div>

      <section className="hobbli-articles__grid">
        {sortedArticles.map((article) => (
          <button
            type="button"
            key={article.id}
            className={`hobbli-article-card ${
              article.featured ? "hobbli-article-card--featured" : ""
            }`}
            onClick={() => openArticle(article)}
          >
            <div className="hobbli-article-card__top">
              <span
                className={`hobbli-article-card__source ${
                  article.source === "hobbli"
                    ? "hobbli-article-card__source--official"
                    : ""
                }`}
              >
                {article.source === "hobbli" ? (
                  <>
                    <FaCheckCircle /> Official
                  </>
                ) : (
                  <>
                    <FaFeatherAlt /> Community
                  </>
                )}
              </span>

              {article.featured && (
                <span className="hobbli-article-card__featured">
                  <FaStar /> Featured
                </span>
              )}
            </div>

            <div className="hobbli-article-card__body">
              <h3>{article.title || "Untitled Article"}</h3>
              <p>{article.summary || "No summary available."}</p>
            </div>

            <div className="hobbli-article-card__meta">
              <span>
                <FaUser /> {article.author_display_name || "Unknown"}
              </span>
              <span>
                <FaCalendarAlt /> {formatDate(article.published_at)}
              </span>
            </div>

            <div className="hobbli-article-card__footer">
              <span>
                <FaThumbsUp /> {article.upvotes_count || 0} Helpful
              </span>
              <strong>Read Guide →</strong>
            </div>
          </button>
        ))}
      </section>

      {sortedArticles.length === 0 && (
        <div className="hobbli-articles__empty">
          <FaBookOpen />
          <h3>No guides found.</h3>
          <p>Try changing your filters or search terms.</p>
        </div>
      )}
    </main>
  );
}
